/**
 * contextEstimator — vibe-designer 1.99.0 (MP3.a)
 *
 * Estimates context-window utilization for the Pi chat conversation so
 * the WARNING banner can fire at ~80% and the ERROR banner can pattern-
 * match canonical context-overflow errors after the agent fails.
 *
 * ADAPTED from pi-coding-agent's compaction.ts:130-289. NOT a verbatim
 * port — the return type of `estimateContextTokens` is intentionally
 * simplified from the upstream `ContextUsageEstimate` object to
 * `number | null`. See contract docstrings for the deltas.
 *
 * Verified file-anchors:
 *   - calculateContextTokens — verbatim of compaction.ts:135-137
 *   - estimateTokens — chars/4 heuristic for the 3 AgentMessage roles
 *     vibe Pi chat produces (user/assistant/toolResult). G0 [A3-2]:
 *     upstream covers 4 more CLI-shaped roles (bashExecution,
 *     branchSummary, compactionSummary, custom) that vibe never produces.
 *   - estimateContextTokens — adaptation of compaction.ts:186-217; uses
 *     last non-error/aborted assistant usage + chars/4 estimate of
 *     trailing messages. Returns null when no assistant turn exists yet
 *     (G0 [A3-1] documented departure from upstream's object return).
 *   - isContextOverflowError — pattern matcher for vLLM/OpenAI/Anthropic
 *     canonical strings.
 *
 * G0 reviewer trail (2026-05-11):
 *   - [A3-1] return type simplified intentionally — banner only needs scalar
 *   - [A3-2] CLI-roles excluded by design
 *   - [B4] pattern set includes "maximum context length" (matches user's
 *     screenshot error text verbatim)
 */
import type { AgentMessage } from "@earendil-works/pi-agent-core";
import type { AssistantMessage, Usage } from "@earendil-works/pi-ai";

/** Threshold for the WARNING banner. Fires when contextTokens/contextWindow >= this. */
export const CONTEXT_WARNING_THRESHOLD = 0.8;

/** Image-block token estimate (matches pi-coding-agent compaction.ts:272 — "4000 chars, or 1200 tokens"). */
const IMAGE_CHAR_ESTIMATE = 4800;

/**
 * Returns total context tokens given a Usage object. Verbatim of
 * pi-coding-agent compaction.ts:135-137 — prefers `totalTokens`, falls
 * back to `input + output + cacheRead + cacheWrite`.
 *
 * G0 [C2]: cacheRead IS included per upstream — cached prompts count
 * toward what the server processed, even if the next request only
 * adds a small new prefix.
 */
export function calculateContextTokens(usage: Usage): number {
  return usage.totalTokens || usage.input + usage.output + usage.cacheRead + usage.cacheWrite;
}

/**
 * Chars/4 heuristic per AgentMessage role. ADAPTED from compaction.ts:232-289.
 *
 * Covers the 3 roles vibe-designer's Pi chat produces:
 *   - `user` — string OR array of {type:'text'|'image', text?, ...}
 *   - `assistant` — array of {type:'text'|'thinking'|'toolCall', ...}
 *   - `toolResult` — string OR array of {type:'text'|'image', ...}
 *
 * Returns 0 for any other role. Upstream also handles `bashExecution`,
 * `branchSummary`, `compactionSummary`, `custom` (G0 [A3-2]) — vibe doesn't
 * produce those, so they fall through to 0.
 *
 * Conservative (overestimates) for covered roles.
 */
export function estimateTokens(message: AgentMessage): number {
  let chars = 0;
  // Loose-cast — AgentMessage's union narrowing is unreliable across the
  // pi-agent-core / pi-ai package boundary.
  const m = message as unknown as {
    role: string;
    content?: unknown;
  };
  switch (m.role) {
    case "user": {
      const content = m.content;
      if (typeof content === "string") {
        chars = content.length;
      } else if (Array.isArray(content)) {
        for (const block of content as Array<{ type: string; text?: string }>) {
          if (block.type === "text" && block.text) chars += block.text.length;
          else if (block.type === "image") chars += IMAGE_CHAR_ESTIMATE;
        }
      }
      return Math.ceil(chars / 4);
    }
    case "assistant": {
      const assistant = message as AssistantMessage;
      for (const block of assistant.content) {
        if (block.type === "text") chars += block.text.length;
        else if (block.type === "thinking") chars += block.thinking.length;
        else if (block.type === "toolCall")
          chars += block.name.length + JSON.stringify(block.arguments).length;
      }
      return Math.ceil(chars / 4);
    }
    case "toolResult": {
      const content = m.content;
      if (typeof content === "string") {
        chars = content.length;
      } else if (Array.isArray(content)) {
        for (const block of content as Array<{ type: string; text?: string }>) {
          if (block.type === "text" && block.text) chars += block.text.length;
          else if (block.type === "image") chars += IMAGE_CHAR_ESTIMATE;
        }
      }
      return Math.ceil(chars / 4);
    }
    default:
      return 0;
  }
}

/**
 * Returns the last assistant message's `usage` IF it has one AND the
 * stopReason is neither 'error' nor 'aborted'. Mirrors compaction.ts:143-151
 * — error/aborted runs produce unreliable usage values.
 */
function getValidAssistantUsage(message: AgentMessage): Usage | undefined {
  if (message.role !== "assistant") return undefined;
  const assistant = message as AssistantMessage;
  if (assistant.stopReason === "error" || assistant.stopReason === "aborted") return undefined;
  return assistant.usage;
}

/**
 * Estimates total conversation tokens. ADAPTED from pi-coding-agent
 * compaction.ts:186-217.
 *
 * Algorithm:
 *   1. Walk messages BACKWARD to find the last non-error/non-aborted
 *      assistant message with `usage`.
 *   2. If found at index `i`:
 *        usageTokens = calculateContextTokens(messages[i].usage)
 *        trailingTokens = sum(estimateTokens(messages[j])) for j > i
 *      Returns `usageTokens + trailingTokens`.
 *   3. If NOT found (initial state, no assistant turn yet): returns `null`
 *      (G0 [A3-1] — banner stays hidden; pointless to warn at "hi" stage).
 *
 * The return type intentionally differs from pi-coding-agent's
 * ContextUsageEstimate object — the banner only needs the scalar ratio.
 */
export function estimateContextTokens(messages: AgentMessage[]): number | null {
  // Find last valid assistant usage
  let lastUsageIndex = -1;
  let lastUsage: Usage | undefined;
  for (let i = messages.length - 1; i >= 0; i--) {
    const usage = getValidAssistantUsage(messages[i]);
    if (usage) {
      lastUsageIndex = i;
      lastUsage = usage;
      break;
    }
  }
  if (!lastUsage) return null;

  let total = calculateContextTokens(lastUsage);
  for (let j = lastUsageIndex + 1; j < messages.length; j++) {
    total += estimateTokens(messages[j]);
  }
  return total;
}

/**
 * Pattern-matches an error string against canonical context-overflow
 * signatures. Returns true if the error is recognized as
 * context-overflow.
 *
 * Patterns cover:
 *   - vLLM: "This model's maximum context length is X tokens..."
 *           "Please reduce the length of the input prompt..."
 *   - OpenAI: "context_length_exceeded"
 *   - Anthropic: "prompt is too long"
 *   - Generic: "context window"
 *
 * Defensive: returns false for null/undefined/empty input.
 */
const CONTEXT_OVERFLOW_PATTERNS = [
  /maximum context length/i,
  /context_length_exceeded/i,
  /prompt is too long/i,
  /reduce the length of (?:the )?input prompt/i,
  /context window/i,
];

export function isContextOverflowError(errorMessage: string | null | undefined): boolean {
  if (!errorMessage || typeof errorMessage !== "string") return false;
  return CONTEXT_OVERFLOW_PATTERNS.some((p) => p.test(errorMessage));
}
